import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import {
  Smartphone,
  CreditCard,
  Landmark,
  Loader2,
  Shield,
  ChevronRight,
  X,
} from 'lucide-react';
import { FEATURES } from '../config/features';
import { useAuth } from '../App';

/**
 * PaymentMethodModal — Razorpay-style unified checkout.
 *
 * User taps a single "Pay ₹X" button → this modal opens →
 * user picks a payment METHOD (UPI / Card / Netbanking) →
 * we route them to the best-suited gateway automatically:
 *
 *   UPI / QR Code      → Instamojo (only gateway with UPI enabled)
 *   Card (Debit/Credit)→ Stripe (default) / Paytm / Instamojo (user picks)
 *   Netbanking         → Paytm (default) / Instamojo (user picks)
 *
 * Gateways that are disabled at the FEATURES flag level, or that would
 * duplicate an already-preferred choice, are simply not rendered.
 * Wallets are intentionally skipped (per product decision).
 */

const METHOD_ICON = {
  upi: Smartphone,
  card: CreditCard,
  netbanking: Landmark,
};

export const PaymentMethodModal = ({
  open,
  onOpenChange,
  amount,
  plan,
  days,
  guestEmail,
  guestName,
  onError,
  // Callbacks the parent passes so we don't duplicate business logic.
  onStripeCheckout, // async () => void — trigger existing handleSubscribe
}) => {
  const [expanded, setExpanded] = useState(null); // 'card' | 'netbanking' | null
  const [routing, setRouting] = useState(null);   // string label of the in-flight route
  const [modalError, setModalError] = useState(null); // inline error banner
  const { token } = useAuth();
  const API = process.env.REACT_APP_BACKEND_URL;

  const resetAndClose = () => {
    setExpanded(null);
    setRouting(null);
    setModalError(null);
    onOpenChange(false);
  };

  const surfaceError = (msg) => {
    // Show the error INSIDE the modal (impossible to miss) AND propagate
    // up so the page-level Alert also fires — belt and braces.
    setModalError(msg);
    if (onError) onError(msg);
  };

  // ---- Gateway routing -----------------------------------------------------

  const startInstamojo = async (label) => {
    setRouting(label);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await axios.post(
        `${API}/api/payments/instamojo/create-order`,
        { plan, days, guest_email: guestEmail, guest_name: guestName },
        { headers, withCredentials: true }
      );
      const { longUrl } = res.data;
      if (!longUrl) throw new Error('Instamojo create-order returned no longUrl');
      window.location.href = longUrl;
    } catch (err) {
      setRouting(null);
      const msg = err?.response?.data?.detail || err?.message || 'Instamojo failed';
      if (onError) onError(`Instamojo error: ${msg}`);
    }
  };

  const startPaytm = async (label) => {
    setRouting(label);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await axios.post(
        `${API}/api/payments/paytm/create-order`,
        { plan, days, guest_email: guestEmail, guest_name: guestName },
        { headers, withCredentials: true }
      );
      const { orderId } = res.data;
      if (!orderId) throw new Error('Paytm create-order returned no orderId');
      // Our /paytm/redirect/{orderId} endpoint serves an auto-submitting
      // HTML form that POSTs to Paytm's showPaymentPage — matches the
      // existing PaytmCheckoutButton flow exactly.
      window.location.href = `${API}/api/payments/paytm/redirect/${orderId}`;
    } catch (err) {
      setRouting(null);
      const msg = err?.response?.data?.detail || err?.message || 'Paytm failed';
      if (onError) onError(`Paytm error: ${msg}`);
    }
  };

  const startSabPaisa = async (label) => {
    setRouting(label);
    setModalError(null);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      // Build payload carefully — never send empty strings for optional
      // fields, or Pydantic's EmailStr validator will 422 on ''.
      const payload = { plan, days };
      if (guestEmail && guestEmail.trim()) payload.guest_email = guestEmail.trim();
      if (guestName && guestName.trim()) payload.customer_name = guestName.trim();
      const res = await axios.post(
        `${API}/api/payments/sabpaisa/create-order`,
        payload,
        { headers, withCredentials: true }
      );
      const { checkout_url } = res.data || {};
      if (!checkout_url) {
        throw new Error('SabPaisa create-order returned no checkout_url');
      }
      window.location.assign(checkout_url);
    } catch (err) {
      setRouting(null);
      // FastAPI validation errors ship `detail` as an array of objects;
      // format them into a human-readable string so we never render
      // "[object Object]" in the modal again.
      const detail = err?.response?.data?.detail;
      let msg;
      if (Array.isArray(detail)) {
        msg = detail
          .map((d) => `${(d.loc || []).slice(1).join('.')}: ${d.msg}`)
          .join('; ');
      } else if (typeof detail === 'string') {
        msg = detail;
      } else if (detail) {
        msg = JSON.stringify(detail);
      } else {
        msg = err?.message || 'SabPaisa failed';
      }
      surfaceError(`SabPaisa error: ${msg}`);
      console.error('[SabPaisa] create-order failed', err);
    }
  };

  const startStripe = async (label) => {
    setRouting(label);
    try {
      await onStripeCheckout();
      // onStripeCheckout is expected to redirect via window.location; we
      // stay on "Redirecting..." until the browser leaves.
    } catch (err) {
      setRouting(null);
      const msg = err?.response?.data?.detail || err?.message || 'Stripe failed';
      if (onError) onError(`Stripe error: ${msg}`);
    }
  };

  // ---- Available options --------------------------------------------------
  // Only surface methods where at least one gateway is enabled.

  const upiAvailable = FEATURES.INSTAMOJO_ENABLED || FEATURES.SABPAISA_ENABLED;
  const cardGateways = [
    FEATURES.STRIPE_ENABLED && { id: 'stripe', label: 'Stripe', tag: 'Recommended', accent: 'text-primary', onPick: () => startStripe('stripe-card') },
    FEATURES.PAYTM_ENABLED && { id: 'paytm', label: 'Paytm', tag: 'India', accent: 'text-[#00B9F5]', onPick: () => startPaytm('paytm-card') },
    FEATURES.SABPAISA_ENABLED && { id: 'sabpaisa', label: 'SabPaisa', tag: 'India', accent: 'text-[#0f766e]', onPick: () => startSabPaisa('sabpaisa-card') },
    FEATURES.INSTAMOJO_ENABLED && { id: 'instamojo', label: 'Instamojo', tag: null, accent: 'text-[#4F3EEC]', onPick: () => startInstamojo('instamojo-card') },
  ].filter(Boolean);
  const netbankingGateways = [
    FEATURES.PAYTM_ENABLED && { id: 'paytm', label: 'Paytm', tag: '40+ banks', accent: 'text-[#00B9F5]', onPick: () => startPaytm('paytm-nb') },
    FEATURES.SABPAISA_ENABLED && { id: 'sabpaisa', label: 'SabPaisa', tag: 'All major banks', accent: 'text-[#0f766e]', onPick: () => startSabPaisa('sabpaisa-nb') },
    FEATURES.INSTAMOJO_ENABLED && { id: 'instamojo', label: 'Instamojo', tag: null, accent: 'text-[#4F3EEC]', onPick: () => startInstamojo('instamojo-nb') },
  ].filter(Boolean);

  // UPI: prefer Instamojo if enabled (1-tap history), else SabPaisa.
  const upiOnClick = FEATURES.INSTAMOJO_ENABLED
    ? () => startInstamojo('instamojo-upi')
    : () => startSabPaisa('sabpaisa-upi');

  const methods = [
    upiAvailable && {
      id: 'upi',
      title: 'UPI / QR Code',
      subtitle: 'GPay, PhonePe, BHIM, Paytm — scan or pay with any UPI app',
      onClick: upiOnClick,
      gateways: null, // 1-tap, no sub-choice
    },
    cardGateways.length > 0 && {
      id: 'card',
      title: 'Card (Debit / Credit)',
      subtitle: 'Visa, Mastercard, Amex, RuPay',
      onClick: () => setExpanded(expanded === 'card' ? null : 'card'),
      gateways: cardGateways,
    },
    netbankingGateways.length > 0 && {
      id: 'netbanking',
      title: 'Net Banking',
      subtitle: 'Choose from all major Indian banks',
      onClick: () => setExpanded(expanded === 'netbanking' ? null : 'netbanking'),
      gateways: netbankingGateways,
    },
  ].filter(Boolean);

  const isRouting = routing !== null;

  return (
    <Dialog open={open} onOpenChange={(v) => !isRouting && onOpenChange(v)}>
      <DialogContent
        className="max-w-lg p-0 gap-0 overflow-hidden sm:rounded-2xl [&>button.absolute]:hidden"
        data-testid="payment-method-modal"
      >
        <DialogTitle className="sr-only">Choose payment method</DialogTitle>
        <DialogDescription className="sr-only">
          Pick UPI, card or netbanking to pay ₹{amount}
        </DialogDescription>

        {/* Header — Razorpay-style */}
        <div className="bg-gradient-to-br from-primary to-primary/80 text-white px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-white/70">Total to pay</p>
            <p className="text-3xl font-black leading-tight" data-testid="modal-total-amount">₹{amount}</p>
            <p className="text-[11px] text-white/70 mt-0.5">
              MISYOU {plan?.charAt(0).toUpperCase() + plan?.slice(1)} · {days} day{days === 1 ? '' : 's'}
            </p>
          </div>
          <button
            onClick={resetAndClose}
            disabled={isRouting}
            className="w-9 h-9 rounded-full hover:bg-white/15 flex items-center justify-center disabled:opacity-40"
            data-testid="close-payment-modal"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Method list */}
        <div className="p-4 space-y-3 max-h-[65vh] overflow-y-auto">
          {/* Inline error banner — visible to the user WITHOUT closing the
              modal, so they can immediately try a different method. */}
          {modalError && (
            <div
              className="rounded-lg bg-red-50 border border-red-200 p-3 flex items-start gap-2"
              data-testid="modal-inline-error"
            >
              <div className="w-5 h-5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">!</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-red-900">Payment method unavailable</p>
                <p className="text-xs text-red-700 mt-0.5 break-words">{modalError}</p>
                <p className="text-[11px] text-red-600 mt-1">Please try a different payment method below.</p>
              </div>
              <button
                onClick={() => setModalError(null)}
                className="text-red-400 hover:text-red-600 text-lg leading-none"
                data-testid="dismiss-modal-error"
              >
                ×
              </button>
            </div>
          )}

          {methods.length === 0 && (
            <div className="text-center py-8 text-sm text-slate-500">
              No payment methods are currently active.
            </div>
          )}

          {methods.map((m) => {
            const Icon = METHOD_ICON[m.id];
            const isExpanded = expanded === m.id;
            const hasSubChoice = !!m.gateways;
            return (
              <div
                key={m.id}
                className="border border-slate-200 rounded-xl overflow-hidden bg-white"
                data-testid={`payment-method-${m.id}`}
              >
                <button
                  onClick={m.onClick}
                  disabled={isRouting}
                  className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900">{m.title}</p>
                    <p className="text-xs text-slate-500 truncate">{m.subtitle}</p>
                  </div>
                  {hasSubChoice ? (
                    <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  )}
                </button>

                {/* Sub-gateway picker (only for Card and Netbanking) */}
                {isExpanded && hasSubChoice && (
                  <div className="px-4 pb-4 pt-1 space-y-2 bg-slate-50 border-t border-slate-100">
                    <p className="text-[10px] uppercase tracking-widest text-slate-500 pt-2">
                      Choose payment partner
                    </p>
                    {m.gateways.map((gw) => (
                      <button
                        key={gw.id}
                        onClick={gw.onPick}
                        disabled={isRouting}
                        className="w-full flex items-center justify-between p-3 rounded-lg bg-white border border-slate-200 hover:border-primary hover:bg-primary/5 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid={`gateway-pick-${m.id}-${gw.id}`}
                      >
                        <span className="flex items-center gap-2">
                          <span className={`font-semibold ${gw.accent}`}>{gw.label}</span>
                          {gw.tag && (
                            <span className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">
                              {gw.tag}
                            </span>
                          )}
                        </span>
                        {routing === `${gw.id}-${m.id}` ? (
                          <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {isRouting && (
            <div className="flex items-center justify-center gap-2 pt-2 text-sm text-slate-600" data-testid="payment-routing-indicator">
              <Loader2 className="w-4 h-4 animate-spin" />
              Redirecting to secure checkout…
            </div>
          )}
        </div>

        {/* Trust footer */}
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-center gap-2 text-[11px] text-slate-600">
          <Shield className="w-3.5 h-3.5" />
          <span>All payments are secured with 256-bit SSL & PCI DSS-compliant partners</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentMethodModal;
